import { latLonToCell } from "./grid.js";
import { intervalAt, rangeMayBeNarrow } from "./uncertainty.js";

export const MM_PER_IN = 25.4;

// Stepped storm-total style palette, thresholds in inches.
export const RAIN_TOTAL_STEPS = [
  [0.01, "#c8e6c9"], [0.1, "#81c784"], [0.25, "#43a047"], [0.5, "#1b5e20"],
  [0.75, "#fff176"], [1, "#fbc02d"], [1.5, "#ff9800"], [2, "#e65100"],
  [2.5, "#e53935"], [3, "#b71c1c"], [4, "#ec407a"], [5, "#ab47bc"],
  [6, "#6a1b9a"], [8, "#90caf9"], [10, "#e3f2fd"], [15, "#ffffff"],
];

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const STEP_RGB = RAIN_TOTAL_STEPS.map(([t, c]) => [t * MM_PER_IN, hexToRgb(c)]);

// Palette color (hex) for a total in mm, or null below the lowest step.
export function rainTotalHex(mm) {
  if (!(mm >= RAIN_TOTAL_STEPS[0][0] * MM_PER_IN)) return null;
  let hex = RAIN_TOTAL_STEPS[0][1];
  for (const [t, c] of RAIN_TOTAL_STEPS) {
    if (mm < t * MM_PER_IN) break;
    hex = c;
  }
  return hex;
}

function colorForMm(mm) {
  if (!(mm >= STEP_RGB[0][0])) return null;
  let rgb = STEP_RGB[0][1];
  for (const [t, c] of STEP_RGB) {
    if (mm < t) break;
    rgb = c;
  }
  return rgb;
}

function renderTotalToCanvas(grid, total) {
  const canvas = document.createElement("canvas");
  canvas.width = grid.nx;
  canvas.height = grid.ny;
  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(grid.nx, grid.ny);
  for (let i = 0; i < total.length; i++) {
    const rgb = colorForMm(total[i]);
    if (!rgb) continue;
    const o = i * 4;
    img.data[o] = rgb[0];
    img.data[o + 1] = rgb[1];
    img.data[o + 2] = rgb[2];
    img.data[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

export function sampleTotal(result, lat, lon) {
  const { grid, total } = result;
  const { col, row } = latLonToCell(grid, lat, lon);
  const c = Math.round(col);
  const r = Math.round(row);
  if (c < 0 || r < 0 || c >= grid.nx || r >= grid.ny) return NaN;
  return total[r * grid.nx + c];
}

export function formatRain(mm, units = "in") {
  if (!Number.isFinite(mm)) return "No coverage";
  return units === "mm" ? `${mm.toFixed(1)} mm` : `${(mm / MM_PER_IN).toFixed(2)} in`;
}

// "0.62–1.40 in": units only once.
export function formatRainRange(loMm, hiMm, units = "in") {
  const [lo, hi] = units === "mm"
    ? [loMm.toFixed(1), hiMm.toFixed(1)]
    : [(loMm / MM_PER_IN).toFixed(2), (hiMm / MM_PER_IN).toFixed(2)];
  return `${lo}–${hi} ${units}`;
}

function buildLegend(L) {
  const Legend = L.Control.extend({
    options: { position: "bottomright" },
    onAdd() {
      const div = L.DomUtil.create("div", "rain-legend");
      div.innerHTML = `<div class="rain-legend-title">Total (in)</div>${RAIN_TOTAL_STEPS.map(
        ([t, c]) => `<div class="rain-legend-row"><span style="background:${c}"></span>${t}</div>`,
      ).join("")}<div class="rain-legend-row rain-legend-gauge"><span></span>Gauge</div>`;
      return div;
    },
  });
  return new Legend();
}

// Shows a rain total on the map with a hover/tap readout.
export function createRainLayer(L, map, { onPick } = {}) {
  let overlay = null;
  let legend = null;
  let result = null;
  let units = "in";
  const readout = L.tooltip({ permanent: false, direction: "top", offset: [0, -8], className: "rain-readout" });

  function readoutHtml(latlng) {
    const mm = sampleTotal(result, latlng.lat, latlng.lng);
    const head = `<strong>${formatRain(mm, units)}</strong>`;
    const iv = Number.isFinite(mm) ? intervalAt(result, latlng.lat, latlng.lng) : null;
    if (!iv) return head;
    return `${head}<div class="rain-readout-range">Gauge likely ${formatRainRange(iv.q05, iv.q95, units)}`
      + `${rangeMayBeNarrow(result) ? " · may be too narrow (radar gap)" : ""}</div>`;
  }

  function onMove(e) {
    if (!result) return;
    readout.setLatLng(e.latlng).setContent(readoutHtml(e.latlng));
    if (!map.hasLayer(readout)) readout.addTo(map);
  }

  function onOut() {
    if (map.hasLayer(readout)) map.removeLayer(readout);
  }

  function onClick(e) {
    if (!result) return;
    onMove(e);
    onPick?.(e.latlng, sampleTotal(result, e.latlng.lat, e.latlng.lng));
  }

  return {
    show(nextResult, opacity = 0.8) {
      this.clear();
      result = nextResult;
      const canvas = renderTotalToCanvas(result.grid, result.total);
      overlay = L.imageOverlay(canvas.toDataURL(), result.grid.bounds, {
        opacity,
        className: "rain-overlay",
        interactive: false,
      }).addTo(map);
      legend = buildLegend(L).addTo(map);
      map.on("mousemove", onMove);
      map.on("mouseout", onOut);
      map.on("click", onClick);
    },
    setUnits(next) {
      units = next;
    },
    setOpacity(opacity) {
      overlay?.setOpacity(opacity);
    },
    clear() {
      if (overlay) map.removeLayer(overlay);
      if (legend) legend.remove();
      onOut();
      map.off("mousemove", onMove);
      map.off("mouseout", onOut);
      map.off("click", onClick);
      overlay = null;
      legend = null;
      result = null;
    },
    get result() {
      return result;
    },
  };
}
